"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { FadeInUp } from "@/components/AnimatedSection";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const budgetOptions = ["< $1K", "$1K – $3K", "$3K – $5K", "$5K – $10K", "$10K+"];

const serviceOptions = [
  { label: "UI/UX Design", icon: "M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" },
  { label: "Web Development", icon: "M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" },
  { label: "Graphic Design", icon: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" },
  { label: "Presentation Design", icon: "M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" },
  { label: "Webflow / Framer", icon: "M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" },
  { label: "AI & Chatbots", icon: "M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" },
  { label: "Mobile App", icon: "M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" },
  { label: "Other", icon: "M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" },
];

const stats = [
  { value: "190+", label: "Projects Delivered" },
  { value: "4+", label: "Years Active" },
  { value: "50+", label: "Happy Clients" },
  { value: "24h", label: "Response Time" },
];

const founders = [
  { name: "Nabeel Imdad", role: "Creative Director", img: "/assets/team/dps/dp_Nabeel Imdad.jpg" },
  { name: "Bilal Ahmad", role: "Tech Lead", img: "/assets/team/dps/dp_Bilal Ahmad.jpg" },
];

const processSteps = [
  { num: "01", title: "Discovery Call", desc: "We learn your goals, timeline, and vision in depth." },
  { num: "02", title: "Proposal & Quote", desc: "Detailed scope, timeline, and transparent pricing." },
  { num: "03", title: "Kickoff & Build", desc: "Design, develop, and deliver — updates throughout." },
];

/* ───────── Animated underline input field ───────── */
function FloatField({
  id,
  label,
  type = "text",
  required = false,
  value,
  onChange,
  activeField,
  onFocus,
  onBlur,
  className = "",
}: {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  activeField: string | null;
  onFocus: () => void;
  onBlur: () => void;
  className?: string;
}) {
  return (
    <div className={`relative group ${className}`}>
      <label
        htmlFor={id}
        className="block text-[10px] font-semibold tracking-widest uppercase text-foreground/30 mb-2 transition-colors group-focus-within:text-red-accent/70"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        className="w-full bg-transparent border-0 border-b border-foreground/10 pb-3 text-sm text-foreground focus:outline-none transition-colors"
      />
      <motion.div
        className="absolute bottom-0 left-0 h-[1.5px] bg-red-accent"
        initial={{ scaleX: 0, originX: 0 }}
        animate={{ scaleX: activeField === id ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        style={{ transformOrigin: "left" }}
      />
    </div>
  );
}

export default function ContactPage() {
  const [selectedBudget, setSelectedBudget] = useState("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [fields, setFields] = useState({ name: "", email: "", phone: "", message: "" });

  const toggleService = (service: string) =>
    setSelectedServices((prev) =>
      prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]
    );

  const copyToClipboard = useCallback((text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState("submitting");

    const subject = encodeURIComponent("Discovery Call Request");
    const body = encodeURIComponent(
      `Name: ${fields.name}\nEmail: ${fields.email}\nPhone: ${fields.phone}\nBudget: ${selectedBudget}\nServices: ${selectedServices.join(", ")}\n\nMessage:\n${fields.message}`
    );
    const mailto = `mailto:thedevcubix@gmail.com?subject=${subject}&body=${body}`;

    window.location.href = mailto;
    setFormState("success");
  };

  return (
    <>
      <Navbar />

      <div className="relative overflow-hidden bg-surface">
        {/* ── Subtle dot-grid background ── */}
        <div
          className="fixed inset-0 pointer-events-none opacity-[0.025]"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* ── Hero red glow ── */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-225 h-125 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center top, rgba(225,29,36,0.18) 0%, transparent 68%)",
          }}
        />

        {/* ════════════════════════════════════
            HERO — Editorial giant heading
        ════════════════════════════════════ */}
        <section className="relative z-10 pt-28 pb-6">
          {/* Logo */}
          <motion.div
            className="absolute top-6 left-1/2 -translate-x-1/2 z-20"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link href="/">
              <img
                src="/assets/devcubix-logo-dark.png"
                alt="Dev Cubix"
                className="h-12 w-auto object-contain dark:hidden block"
              />
              <img
                src="/assets/devcubix-logo-white.png"
                alt="Dev Cubix"
                className="h-12 w-auto object-contain hidden dark:block"
              />
            </Link>
          </motion.div>

          <div className="max-w-300 mx-auto px-6">
            <FadeInUp>
              <div className="text-center">
                <p className="section-badge mb-6 mx-auto">Start a Project With Us</p>
                <h1 className="text-3xl sm:text-4xl md:text-[64px] font-bold leading-[1.1] font-ui mb-6">
                  Let&apos;s Build{" "}
                  <span className="font-serif italic text-purple-light">Something</span>
                  <br />
                  <span className="font-serif italic text-purple-light">Great</span>{" "}Together
                </h1>
                <p className="text-foreground/50 text-lg max-w-xl mx-auto font-body">
                  Tell us about your project and we&apos;ll get back to you within 24 hours.
                </p>
              </div>
            </FadeInUp>
          </div>
        </section>

        {/* ════════════════════════════════════
            STATS STRIP
        ════════════════════════════════════ */}
        <section className="relative z-10 border-b border-foreground/6">
          <div className="max-w-300 mx-auto px-6 py-7">
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.75 }}
            >
              {stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.8 + i * 0.07 }}
                  className="flex flex-col items-center text-center py-2 border-r border-foreground/6 last:border-r-0"
                >
                  <span
                    className="font-black font-ui text-foreground"
                    style={{ fontSize: "clamp(26px, 3.5vw, 40px)" }}
                  >
                    {stat.value}
                  </span>
                  <span className="text-[10px] text-foreground/30 tracking-widest uppercase mt-1">
                    {stat.label}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ════════════════════════════════════
            MAIN CONTENT — 2 columns
        ════════════════════════════════════ */}
        <section className="relative z-10 py-16 pb-24">
          <div className="max-w-300 mx-auto px-6">
            <div className="grid lg:grid-cols-[380px_1fr] gap-12 xl:gap-20 items-start">

              {/* ─────────────────────────────
                  LEFT — Info panel (sticky)
              ───────────────────────────── */}
              <motion.aside
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="lg:sticky lg:top-28 space-y-8"
              >
                {/* Section label */}
                <div className="flex items-center gap-3">
                  <span className="text-red-accent font-mono text-[11px] tracking-widest">§ 01</span>
                  <div className="flex-1 h-px bg-foreground/[0.07]" />
                  <span className="text-foreground/25 text-[10px] tracking-widest uppercase">Reach Us</span>
                </div>

                {/* Process timeline */}
                <div className="relative">
                  <div className="absolute left-3.75 top-8 bottom-4 w-px bg-linear-to-b from-red-accent/40 via-foreground/8 to-transparent" />
                  {processSteps.map((step, i) => (
                    <motion.div
                      key={step.num}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 1.05 + i * 0.1 }}
                      className="relative flex gap-5 mb-7 last:mb-0"
                    >
                      <div className="relative z-10 w-8 h-8 rounded-full bg-surface border border-red-accent/30 flex items-center justify-center shrink-0">
                        <span className="text-[10px] font-bold text-red-accent font-mono">{step.num}</span>
                      </div>
                      <div className="pt-1">
                        <p className="font-semibold text-sm font-ui mb-0.5">{step.title}</p>
                        <p className="text-foreground/35 text-xs font-body leading-relaxed">{step.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Founders card */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 1.35 }}
                  className="p-5 rounded-2xl border border-foreground/[0.07] bg-surface-light/50"
                >
                  <p className="text-[10px] font-semibold text-foreground/25 tracking-widest uppercase mb-4">
                    Meet the Team
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {founders.map((person) => (
                      <div key={person.name} className="flex items-center gap-3 min-w-0">
                        <div className="relative">
                          <img
                            src={person.img}
                            alt={person.name}
                            className="w-11 h-11 rounded-full object-cover ring-2 ring-red-accent/20"
                          />
                          <span className="absolute -bottom-px -right-px w-3 h-3 bg-emerald-400 rounded-full border-2 border-surface-light" />
                        </div>
                        <div>
                          <p className="font-semibold text-xs font-ui leading-tight">{person.name}</p>
                          <p className="text-[10px] text-foreground/35 mt-0.5">{person.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Contact channels */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 1.45 }}
                  className="space-y-2"
                >
                  {/* WhatsApp */}
                  <Link
                    href="https://wa.me/+923190708852"
                    className="group flex items-center gap-4 p-3.5 rounded-xl border border-foreground/[0.07] hover:border-red-accent/30 hover:bg-red-accent/4 transition-all"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#25D366]/10 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold font-ui">WhatsApp</p>
                      <p className="text-[11px] text-foreground/35 mt-0.5">+92 319 0708852</p>
                    </div>
                    <svg
                      className="w-4 h-4 text-foreground/20 group-hover:text-foreground/50 group-hover:translate-x-0.5 transition-all"
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>

                  {/* Email — click to copy */}
                  <button
                    type="button"
                    onClick={() => copyToClipboard("info@devcubix.com", "email")}
                    className="group w-full flex items-center gap-4 p-3.5 rounded-xl border border-foreground/[0.07] hover:border-red-accent/30 hover:bg-red-accent/4 transition-all text-left"
                  >
                    <div className="w-9 h-9 rounded-lg bg-red-accent/10 flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold font-ui">Email</p>
                      <p className="text-[11px] text-foreground/35 mt-0.5">info@devcubix.com</p>
                    </div>
                    <AnimatePresence mode="wait">
                      {copiedField === "email" ? (
                        <motion.span
                          key="copied"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          className="text-[10px] text-badge-text font-mono tracking-wide"
                        >
                          Copied!
                        </motion.span>
                      ) : (
                        <svg key="icon" className="w-4 h-4 text-foreground/20 group-hover:text-foreground/50 transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                        </svg>
                      )}
                    </AnimatePresence>
                  </button>

                  {/* Location */}
                  <a
                    href="https://www.google.com/maps/place/DevCubix+Private+Ltd./@33.5198,73.0898,17z/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-start gap-4 p-3.5 rounded-xl border border-foreground/[0.07] hover:border-red-accent/30 hover:bg-red-accent/4 transition-all"
                  >
                    <div className="w-9 h-9 rounded-lg bg-foreground/5 flex items-center justify-center shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-foreground/35" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold font-ui mb-0.5">Office</p>
                      <p className="text-[11px] text-foreground/35 leading-relaxed">
                        #A5 Federal Arcade, Chackshehzad<br />
                        Islamabad, Pakistan
                      </p>
                    </div>
                  </a>
                </motion.div>

                {/* Decorative quote */}
                <motion.blockquote
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 1.6 }}
                  className="border-l-2 border-red-accent/40 pl-4 py-1"
                >
                  <p className="font-serif italic text-foreground/30 text-sm leading-relaxed">
                    &ldquo;Great design starts with a great conversation.&rdquo;
                  </p>
                </motion.blockquote>
              </motion.aside>

              {/* ─────────────────────────────
                  RIGHT — Project Brief form
              ───────────────────────────── */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
              >
                {/* Section label */}
                <div className="flex items-center gap-3 mb-8">
                  <span className="text-red-accent font-mono text-[11px] tracking-widest">§ 02</span>
                  <div className="flex-1 h-px bg-foreground/[0.07]" />
                  <span className="text-foreground/25 text-[10px] tracking-widest uppercase">Project Brief</span>
                </div>

                <AnimatePresence mode="wait">
                  {formState === "success" ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="py-28 flex flex-col items-center text-center"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 240, delay: 0.1 }}
                        className="w-20 h-20 rounded-full bg-red-accent/10 border border-red-accent/30 flex items-center justify-center mb-6"
                      >
                        <svg className="w-10 h-10 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </motion.div>
                      <motion.h3
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                        className="text-4xl font-black font-ui mb-3 tracking-tight"
                      >
                        Brief Sent.
                      </motion.h3>
                      <motion.p
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35 }}
                        className="text-foreground/40 font-body text-base mb-8 max-w-sm"
                      >
                        We&apos;ll review your project and reach out within 24 hours.
                      </motion.p>
                      <button
                        onClick={() => setFormState("idle")}
                        className="text-sm text-badge-text hover:text-foreground transition-colors underline underline-offset-4"
                      >
                        Start a new brief
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      onSubmit={handleSubmit}
                      className="space-y-4"
                    >
                      {/* ── Block 1: Contact Details ── */}
                      <div className="border border-foreground/[0.07] rounded-2xl p-6 bg-surface-light/30 hover:border-foreground/12 transition-colors">
                        <p className="text-[10px] font-semibold text-foreground/25 tracking-widest uppercase mb-6">
                          Contact Details
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                          <FloatField
                            id="name"
                            label="Full Name"
                            required
                            value={fields.name}
                            onChange={(v) => setFields((f) => ({ ...f, name: v }))}
                            activeField={activeField}
                            onFocus={() => setActiveField("name")}
                            onBlur={() => setActiveField(null)}
                            className="sm:col-span-3"
                          />
                          <FloatField
                            id="email"
                            label="Email Address"
                            type="email"
                            required
                            value={fields.email}
                            onChange={(v) => setFields((f) => ({ ...f, email: v }))}
                            activeField={activeField}
                            onFocus={() => setActiveField("email")}
                            onBlur={() => setActiveField(null)}
                            className="sm:col-span-2"
                          />
                          <FloatField
                            id="phone"
                            label="WhatsApp No."
                            type="tel"
                            value={fields.phone}
                            onChange={(v) => setFields((f) => ({ ...f, phone: v }))}
                            activeField={activeField}
                            onFocus={() => setActiveField("phone")}
                            onBlur={() => setActiveField(null)}
                          />
                        </div>
                      </div>

                      {/* ── Block 2: Services ── */}
                      <div className="border border-foreground/[0.07] rounded-2xl p-6 bg-surface-light/30 hover:border-foreground/12 transition-colors">
                        <p className="text-[10px] font-semibold text-foreground/25 tracking-widest uppercase mb-5">
                          What do you need?
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {serviceOptions.map((service, i) => {
                            const isSelected = selectedServices.includes(service.label);
                            return (
                              <motion.button
                                key={service.label}
                                type="button"
                                onClick={() => toggleService(service.label)}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 1.1 + i * 0.04 }}
                                whileTap={{ scale: 0.96 }}
                                className={`relative flex flex-col items-center gap-2 p-3.5 rounded-xl border text-center transition-all overflow-hidden ${
                                  isSelected
                                    ? "border-red-accent bg-red-accent/10 text-red-accent"
                                    : "border-foreground/8 bg-surface/40 text-foreground/35 hover:border-foreground/20 hover:text-foreground/60"
                                }`}
                              >
                                {isSelected && (
                                  <motion.div
                                    className="absolute inset-0 bg-red-accent/5"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                  />
                                )}
                                <svg className="w-5 h-5 relative z-10 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d={service.icon} />
                                </svg>
                                <span className="text-[11px] font-medium relative z-10 leading-tight">{service.label}</span>
                                {isSelected && (
                                  <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute top-2 right-2 w-3.5 h-3.5 rounded-full bg-red-accent flex items-center justify-center z-10"
                                  >
                                    <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  </motion.span>
                                )}
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>

                      {/* ── Block 3: Budget + Message ── */}
                      <div className="border border-foreground/[0.07] rounded-2xl p-6 bg-surface-light/30 hover:border-foreground/12 transition-colors">
                        <div className="mb-7">
                          <p className="text-[10px] font-semibold text-foreground/25 tracking-widest uppercase mb-4">
                            Project Budget
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {budgetOptions.map((opt) => (
                              <motion.button
                                key={opt}
                                type="button"
                                onClick={() => setSelectedBudget(opt)}
                                whileTap={{ scale: 0.95 }}
                                className={`px-5 py-2.5 rounded-full text-xs font-semibold border transition-all ${
                                  selectedBudget === opt
                                    ? "border-red-accent bg-red-accent/10 text-red-accent"
                                    : "border-foreground/8 text-foreground/35 hover:border-foreground/20 hover:text-foreground/60"
                                }`}
                              >
                                {opt}
                              </motion.button>
                            ))}
                          </div>
                        </div>

                        {/* Message */}
                        <div className="relative group">
                          <label
                            htmlFor="message"
                            className="block text-[10px] font-semibold tracking-widest uppercase text-foreground/25 mb-2 transition-colors group-focus-within:text-red-accent/70"
                          >
                            Project Description
                          </label>
                          <textarea
                            id="message"
                            required
                            rows={5}
                            placeholder="Tell us about your project — goals, timeline, references..."
                            value={fields.message}
                            onChange={(e) => setFields((f) => ({ ...f, message: e.target.value }))}
                            onFocus={() => setActiveField("message")}
                            onBlur={() => setActiveField(null)}
                            className="w-full bg-transparent border-0 border-b border-foreground/10 pb-3 text-sm text-foreground placeholder:text-foreground/20 focus:outline-none transition-colors resize-none"
                          />
                          <motion.div
                            className="absolute bottom-0 left-0 h-[1.5px] bg-red-accent"
                            initial={{ scaleX: 0, originX: 0 }}
                            animate={{ scaleX: activeField === "message" ? 1 : 0 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            style={{ transformOrigin: "left" }}
                          />
                        </div>
                      </div>

                      {/* ── Submit ── */}
                      <motion.button
                        type="submit"
                        disabled={formState === "submitting"}
                        onClick={() => formState === "error" && setFormState("idle")}
                        whileHover={{ scale: 1.005 }}
                        whileTap={{ scale: 0.995 }}
                        className="relative w-full py-5 bg-red-accent text-white font-bold rounded-2xl text-[15px] overflow-hidden group disabled:opacity-70 transition-opacity"
                      >
                        {/* Shimmer sweep on hover */}
                        <motion.div
                          className="absolute inset-0 bg-linear-to-r from-transparent via-white/10 to-transparent"
                          initial={{ x: "-110%" }}
                          whileHover={{ x: "110%" }}
                          transition={{ duration: 0.65, ease: "easeInOut" }}
                        />
                        <span className="relative z-10 flex items-center justify-center gap-3 font-ui tracking-wide">
                          {formState === "submitting" ? (
                            <>
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Sending Brief...
                            </>
                          ) : (
                            <>
                              Send Project Brief
                              <svg
                                className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </>
                          )}
                        </span>
                      </motion.button>

                      {formState === "error" && (
                        <p className="text-center text-xs text-red-accent pt-1">
                          Something went wrong. Please try WhatsApp or email us directly.
                        </p>
                      )}
                      {formState !== "error" && (
                        <p className="text-center text-[11px] text-foreground/20 pt-1">
                          We respond within 24 hours. No spam, ever.
                        </p>
                      )}
                    </motion.form>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}
